/**
 * Zustand store for UI preferences (language + theme + palette).
 *
 * UI-only data — NO PII. Persisted to localStorage so the toggles
 * survive reload (AC-2). The key prefix `garden.ui.` is shared with
 * i18n.ts's LanguageDetector lookupLocalStorage so the two stay in sync.
 *
 * `language` mirrors the i18next active language; calling `setLanguage`
 * also calls `i18n.changeLanguage`. The setter is the authoritative
 * write path — components should never set localStorage directly.
 *
 * `palette` is one of the four GardEn brand palettes (design-revamp,
 * handoff-2026-06). It maps to the `data-palette` attribute that
 * `ThemeController` writes onto <html>; `theme.css` resolves the
 * `--g-primary`/`--g-accent`/soft tints for the active palette. Default
 * `"teal"` matches the no-attribute baseline so nothing changes visually
 * until the user (or a future picker) opts into another palette.
 */
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

import i18n, { type SupportedLanguage } from "@/lib/i18n";

export type Theme = "light" | "dark" | "system";

/** The four GardEn brand palettes (GARDEN_PALETTES in system.jsx). */
export type Palette = "teal" | "sage" | "terracotta" | "indigo";

export interface UiState {
  language: SupportedLanguage;
  theme: Theme;
  palette: Palette;
  setLanguage: (lng: SupportedLanguage) => void;
  setTheme: (theme: Theme) => void;
  setPalette: (palette: Palette) => void;
}

export const UI_STORE_LANGUAGE_KEY = "garden.ui.language";
export const UI_STORE_PERSIST_KEY = "garden.ui";

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      language: "ru",
      theme: "system",
      palette: "teal",
      setLanguage: (language) => {
        // Sync i18next so visible strings switch immediately.
        if (i18n.isInitialized) {
          void i18n.changeLanguage(language);
        }
        // Also mirror the raw value to the legacy key the LanguageDetector
        // reads, so a hard reload still picks the right language even
        // before the persist middleware re-hydrates the store.
        try {
          window.localStorage.setItem(UI_STORE_LANGUAGE_KEY, language);
        } catch {
          // Fail silently if storage is unavailable (private mode, SSR).
        }
        set({ language });
      },
      setTheme: (theme) => set({ theme }),
      setPalette: (palette) => set({ palette }),
    }),
    {
      name: UI_STORE_PERSIST_KEY,
      storage: createJSONStorage(() => window.localStorage),
      partialize: (state) => ({
        language: state.language,
        theme: state.theme,
        palette: state.palette,
      }),
    },
  ),
);
